import { api } from '@/lib/api/client';
import type { LoginFailure, LoginSuccess } from '@/types/user';

/**
 * Signing in.
 *
 * The one endpoint that takes no token — and the one whose failures do not use a
 * status code. A wrong password answers **200 OK** with an `errorCode` body, so
 * `response.ok` is true for a login that did not happen. Every caller has to look
 * at the body, which is why this module returns a discriminated union: there is no
 * way to read the result without checking which case it is.
 */

export type LoginResult =
  | { ok: true; session: LoginSuccess }
  | { ok: false; message: string; errorCode: string };

/** What the endpoint may answer with, before we know which it was. */
type RawLoginResponse = Partial<LoginSuccess> & Partial<LoginFailure>;

export const authenticate = async (username: string, password: string): Promise<LoginResult> => {
  const response = await api.post<RawLoginResponse>('/login/authenticate', { username, password });

  // Success is "a token came back", failure is "an errorCode came back". Checked
  // in that order because a body carrying both would be a server bug, and taking
  // the token would then sign someone in on a failure.
  if (response.errorCode) {
    return {
      ok: false,
      errorCode: response.errorCode,
      // The server's message is in Russian regardless of the UI language. Callers
      // decide whether to show it or their own translated text.
      message: response.message ?? '',
    };
  }

  if (response.token && response.userInfo) {
    return {
      ok: true,
      session: {
        token: response.token,
        elkToken: response.elkToken ?? null,
        userInfo: response.userInfo,
      },
    };
  }

  // Neither shape: a proxy in the way, or an endpoint that changed under us.
  // Saying so beats pretending the password was wrong.
  return { ok: false, errorCode: 'MALFORMED_RESPONSE', message: '' };
};

/**
 * A fresh token for a wall screen — `DISPLAY` only; the server answers 403 to
 * anyone else. The same body as a sign-in, without the failure case: an ended
 * session answers 401, which the API client turns into a sign-out like any other.
 */
export const renewSession = async (): Promise<LoginSuccess> => {
  const response = await api.post<Partial<LoginSuccess>>('/login/refresh');

  if (!response.token || !response.userInfo) {
    throw new Error('The refresh answered without a token');
  }

  return { token: response.token, elkToken: response.elkToken ?? null, userInfo: response.userInfo };
};

/**
 * The current user, straight from the server.
 *
 * `/user/me` is reachable by any signed-in role, so this doubles as "is the stored
 * token still good" — a blocked account or a changed role shows up here on the
 * next call rather than after five hours.
 */
export const fetchCurrentUser = () => api.get<LoginSuccess['userInfo']>('/user/me');
